import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Company from './pages/Company';
import Divisions from './pages/Divisions';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Responsibility from './pages/Responsibility';
import Blog from './pages/Blog';
import Careers from './pages/Careers';
import Contact from './pages/Contact';
import Preloader from './components/Preloader';
import './index.css';
import { ToastProvider } from './components/Toast';
import BulkImporter from './components/Bulkimporter';

const AdminGuard              = lazy(() => import('./pages/admin/AdminGuard'));
const AdminShell              = lazy(() => import('./pages/admin/AdminShell'));
const AdminLogin              = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard          = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminDivisions          = lazy(() => import('./pages/admin/AdminDivisions'));
const AdminCategories         = lazy(() => import('./pages/admin/AdminCategories'));
const AdminChemicalCategories = lazy(() => import('./pages/admin/AdminChemicalCategories'));
const AdminProducts           = lazy(() => import('./pages/admin/AdminProducts'));
const AdminProductsReport     = lazy(() => import('./pages/admin/AdminProductsReport'));

/* ── Smooth custom cursor ─────────────────────────── */
function SmoothCursor() {
  useEffect(() => {
    const dot  = document.getElementById('custom-cursor');
    const ring = document.getElementById('custom-cursor-ring');
    if (!dot || !ring) return;

    let mouseX = 0, mouseY = 0, ringX = 0, ringY = 0, raf;

    const onMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      dot.style.left = mouseX + 'px';
      dot.style.top  = mouseY + 'px';
    };

    const lerp = (a, b, t) => a + (b - a) * t;
    const animate = () => {
      ringX = lerp(ringX, mouseX, 0.1);
      ringY = lerp(ringY, mouseY, 0.1);
      ring.style.left = ringX + 'px';
      ring.style.top  = ringY + 'px';
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);

    const onEnter = () => {
      dot.style.width   = '6px';
      dot.style.height  = '6px';
      dot.style.opacity = '0.5';
      ring.style.width       = '56px';
      ring.style.height      = '56px';
      ring.style.borderColor = 'rgba(46,180,180,0.9)';
      ring.style.background  = 'rgba(46,180,180,0.06)';
    };
    const onLeave = () => {
      dot.style.width   = '12px';
      dot.style.height  = '12px';
      dot.style.opacity = '1';
      ring.style.width       = '36px';
      ring.style.height      = '36px';
      ring.style.borderColor = 'rgba(46,180,180,0.5)';
      ring.style.background  = 'transparent';
    };

    const addHover = () => {
      document.querySelectorAll('a, button, [role="button"], input, select, textarea, label')
        .forEach(el => {
          el.removeEventListener('mouseenter', onEnter);
          el.removeEventListener('mouseleave', onLeave);
          el.addEventListener('mouseenter', onEnter);
          el.addEventListener('mouseleave', onLeave);
        });
    };

    window.addEventListener('mousemove', onMove);
    addHover();

    const mo = new MutationObserver(addHover);
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(raf);
      mo.disconnect();
    };
  }, []);

  return (
    <>
      <div id="custom-cursor" />
      <div id="custom-cursor-ring" />
    </>
  );
}

/* ── Scroll to top on route change ───────────────── */
function ScrollToAnchor() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const t = setTimeout(() => {
      if (hash) {
        const el = document.getElementById(hash.slice(1));
        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
      }
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, 60);
    return () => clearTimeout(t);
  }, [pathname, hash]);
  return null;
}

/* ── Scroll reveal observer ──────────────────────── */
function RevealObserver() {
  const { pathname } = useLocation();
  const observerRef = useRef(null);

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();
    const t = setTimeout(() => {
      observerRef.current = new IntersectionObserver(
        (entries) => entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
            observerRef.current?.unobserve(e.target);
          }
        }),
        { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
      );
      document.querySelectorAll('.reveal:not(.visible)')
        .forEach(el => observerRef.current?.observe(el));
    }, 280);
    return () => {
      clearTimeout(t);
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [pathname]);

  return null;
}

/*
  ── Page transition ─────────────────────────────────
  Technique: keep BOTH old and new pages mounted simultaneously
  during the transition. Old page fades+slides out while new page
  fades+slides in. Using absolute positioning so they overlap.
  This avoids the "content snap" that happens when you swap children
  before the exit animation finishes.
*/
function PageTransition({ children }) {
  const location = useLocation();
  const TRANSITION_MS = 620;

  // Each entry: { key, children, phase: 'enter'|'idle'|'exit' }
  const [pages, setPages] = useState([
    { key: location.key || location.pathname, node: children, phase: 'idle' }
  ]);

  const prevLocation = useRef(location);

  useEffect(() => {
    if (location.pathname === prevLocation.current.pathname) return;
    prevLocation.current = location;

    const incomingKey = location.key || location.pathname;

    setPages(prev => {
      // Mark all existing pages as exiting
      const exiting = prev.map(p => ({ ...p, phase: 'exit' }));
      // Add new page in 'enter' phase
      return [...exiting, { key: incomingKey, node: children, phase: 'enter' }];
    });

    const enterFrame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setPages(prev =>
          prev.map(p =>
            p.key === incomingKey && p.phase === 'enter'
              ? { ...p, phase: 'idle' }
              : p
          )
        );
      });
    });

    // After exit animation, remove old pages
    const removeTimer = setTimeout(() => {
      setPages(prev => prev.filter(p => p.key === incomingKey));
    }, TRANSITION_MS);

    return () => {
      cancelAnimationFrame(enterFrame);
      clearTimeout(removeTimer);
    };
  }, [location.pathname]); // eslint-disable-line

  // Update the current page's children when they change (e.g. same route re-render)
  useEffect(() => {
    setPages(prev =>
      prev.map(p =>
        p.phase === 'idle' || p.phase === 'enter'
          ? { ...p, node: children }
          : p
      )
    );
  }, [children]);

  const hasMultiple = pages.length > 1;

  return (
    <div style={{ position: 'relative', minHeight: hasMultiple ? '100vh' : undefined }}>
      {pages.map(({ key, node, phase }) => (
        <div
          key={key}
          style={{
            position:   hasMultiple ? 'absolute' : 'relative',
            inset:      hasMultiple ? 0 : undefined,
            width:      '100%',
            transition: 'opacity 0.62s cubic-bezier(0.16,1,0.3,1), transform 0.62s cubic-bezier(0.16,1,0.3,1), filter 0.62s cubic-bezier(0.16,1,0.3,1)',
            opacity:    phase === 'exit'  ? 0
                      : phase === 'enter' ? 0
                      : 1,
            transform:  phase === 'exit'  ? 'translateY(12px)'
                      : phase === 'enter' ? 'translateY(22px)'
                      : 'translateY(0)',
            filter:     phase === 'idle' ? 'blur(0)' : 'blur(3px)',
            pointerEvents: phase === 'exit' ? 'none' : 'all',
            zIndex:     phase === 'exit' ? 0 : 1,
            willChange: 'opacity, transform, filter',
          }}
        >
          {node}
        </div>
      ))}
    </div>
  );
}

/* ── Animated routes ─────────────────────────────── */
function AnimatedRoutes() {
  const location = useLocation();
  const isAdmin  = location.pathname.startsWith('/admin');

  if (isAdmin) {
    return (
      <Suspense fallback={
        <div style={{
          minHeight: '100vh',
          background: '#f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#64748b',
          fontFamily: 'DM Sans, sans-serif',
        }}>
          Loading admin…
        </div>
      }>
        <Routes location={location}>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route element={<AdminGuard />}>
            <Route element={<AdminShell />}>
              <Route path="/admin"                             element={<AdminDashboard />} />
              <Route path="/admin/divisions"                   element={<AdminDivisions />} />
              <Route path="/admin/product-categories"          element={<AdminCategories />} />
              <Route path="/admin/product-chemical-categories" element={<AdminChemicalCategories />} />
              <Route path="/admin/products"                    element={<AdminProducts />} />
              <Route path="/admin/products-report"             element={<AdminProductsReport />} />  {/* ← add this */}
            </Route>
          </Route>
        </Routes>
      </Suspense>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navbar />
      <main style={{ flex: 1 }}>
        <PageTransition>
          <Routes location={location}>
            <Route path="/"               element={<Home />} />
            <Route path="/company"        element={<Company />} />
            <Route path="/divisions"      element={<Divisions />} />
            <Route path="/products"       element={<Products />} />
            <Route path="/products/:id"   element={<ProductDetail />} />
            <Route path="/responsibility" element={<Responsibility />} />
            <Route path="/blog"           element={<Blog />} />
            <Route path="/careers"        element={<Careers />} />
            <Route path="/contact"        element={<Contact />} />
            {/* <Route path="/bulk-import" element={<BulkImporter />} /> */}
          </Routes>
        </PageTransition>
      </main>
      <Footer />
    </div>
  );
}

/* ── App root ────────────────────────────────────── */
export default function App() {
  // Always show preloader on every page load / hard refresh.
  // useState(true) means it's always shown when the module mounts fresh.
  // In-app navigation does NOT remount App, so preloader won't re-trigger.
  const [preloading, setPreloading] = useState(true);

  return (
    <ToastProvider>
      {preloading && <Preloader onComplete={() => setPreloading(false)} />}

      {/* App is mounted immediately but invisible until preloader exits */}
      <div style={{
        opacity:    preloading ? 0 : 1,
        transition: 'opacity 0.5s ease 0.15s',
      }}>
        <Router>
          <SmoothCursor />
          <ScrollToAnchor />
          <RevealObserver />
          <AnimatedRoutes />
        </Router>
      </div>
    </ToastProvider>
  );
}
